import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { DotsNine } from "@phosphor-icons/react";
import clsx from "clsx";
import { shuffle } from "lodash-es";
import type React from "react";
import { use, useCallback, useMemo, useState } from "react";
import { match } from "ts-pattern";

import { useGame } from "~/components/GameContext";
import { useTheme } from "~/hooks/theme";

import { GameClue } from "../GameClue";
import { GamePlayContext } from "../GamePlayContext";
import type { GameAnswer } from "../utils/answer";
import { Draggable } from "./Draggable";
import { Droppable } from "./Droppable";
import Item from "./Item";
import { PairsGrid } from "./PairsGrid";

const MAX_WORDS_PER_LEVEL = 4;

// TODO: call this "pairs"?
export const Pairs: React.FC = () => {
  const { game, playSound } = useGame();
  const { addAnswer, addRewardTime, answers, blastConfetti, endGame } =
    use(GamePlayContext);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);

  const connectedWordIds = useMemo(() => {
    const wordIds = game.answers
      .concat(answers)
      .filter((answer) => answer.correct)
      .map((answer) => answer.word.id);
    return [...new Set(wordIds)];
  }, [game.answers, answers]);

  const level = useMemo(
    () => Math.floor(connectedWordIds.length / MAX_WORDS_PER_LEVEL),
    [connectedWordIds],
  );

  const wordsThisLevel = useMemo(() => {
    const offset = level * MAX_WORDS_PER_LEVEL;
    return game.words.slice(offset, offset + MAX_WORDS_PER_LEVEL);
  }, [game.words, level]);

  const shuffledWords = useMemo(
    () => shuffle(wordsThisLevel),
    [wordsThisLevel],
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null);

      const word = game.words.find((word) => word.id === event.over?.id);
      if (word) {
        if (connectedWordIds.includes(word.id)) {
          // if the word is already connected, skip it
          return;
        }

        const answer: GameAnswer = {
          correct: event.over?.id === event.active.id,
          word,
          solution: word.solution,
        };

        if (
          // it's a match!
          answer.correct &&
          // and it's the last word in the game
          connectedWordIds.length + 1 === game.words.length
        ) {
          endGame(answer);
          playSound("earn_hint");
          return;
        }

        addAnswer(answer);

        if (answer.correct) {
          const isLastWordThisLevel =
            connectedWordIds.length + 1 ===
            wordsThisLevel.length + MAX_WORDS_PER_LEVEL * level;

          if (isLastWordThisLevel) {
            playSound("gem");
            blastConfetti();
            addRewardTime(12); // 12 seconds for completing a level
            return;
          }

          playSound("snap");
          return;
        }

        playSound("pop");
      }
    },
    [
      addAnswer,
      playSound,
      endGame,
      level,
      wordsThisLevel.length,
      connectedWordIds,
      game.words,
      addRewardTime,
      blastConfetti,
    ],
  );

  const [theme] = useTheme();
  const isDefaultTheme = useMemo(
    () =>
      match(theme)
        .with("dark", "knoword", () => true)
        .otherwise(() => false),
    [theme],
  );

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(KeyboardSensor),
    useSensor(TouchSensor),
  );

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className={clsx("relative", isDefaultTheme && "bg-base-200")}>
        <PairsGrid>
          {shuffledWords.map((word) => {
            if (connectedWordIds.includes(word.id)) {
              return <div key={word.id} />;
            }

            return (
              <Draggable key={word.id} id={word.id}>
                <Item>
                  {word.solution}
                  <DotsNine className="absolute right-1 top-1 size-6" />
                </Item>
              </Draggable>
            );
          })}
        </PairsGrid>
        <DragOverlay className="grid cursor-grabbing overflow-hidden rounded border shadow">
          {activeId ? (
            <Item>
              {game.words.find((word) => word.id === activeId)?.solution}
            </Item>
          ) : null}
        </DragOverlay>
      </div>
      <div className="relative bg-base-100/60 backdrop-blur">
        <PairsGrid>
          {wordsThisLevel.map((word) => {
            const isConnected = connectedWordIds.includes(word.id);
            return (
              <Droppable key={word.id} id={word.id} disabled={isConnected}>
                {isConnected && <Item>{word.solution}</Item>}
                <div className="relative flex grow items-center justify-center text-center">
                  <GameClue
                    size="sm"
                    word={word}
                    autoPlay={false}
                    isSpeechEnabled={!isConnected}
                  />
                </div>
              </Droppable>
            );
          })}
        </PairsGrid>
      </div>
    </DndContext>
  );
};
